import React, { useCallback, useContext, useMemo, useState } from "react";
import ColorPicker from "../../Shared/ColorPicker";
import ColumnWeightInput from "../ColumnWeightInput/ColumnWeightInput";
import "./CreateColumnForm.css";
import { IFunctionPermissions } from "../../../common/api_interfaces/role/IFunctionPermissions";
import { AppContext } from "../../../App";
import { hasPermission } from "../../../helpers/hasPermisson";
import ColumnStatusChange from "../ColumnStatusChange/ColumnStatusChange";
import { BoardContext } from "../../../contexts/BoardContext";

export interface ICreateColumnForm {
    userAccess: IFunctionPermissions[];
}

const MIN_CHARACTERS = 5;

const CreateColumnForm: React.FC<ICreateColumnForm> = ({ userAccess }) => {
    const { showToast } = useContext(AppContext);
    const { editStatusAcess: editStatusAccess } = useContext(BoardContext);
    const boardContext = useContext(BoardContext);

    const [addProxyColumn, setAddProxyColumn] = useState(false);
    const [newColumnName, setNewColumnName] = useState("");
    const [proxyBoardName, setProxyBoardName] = useState("");
    const [columnColor, setColumnColor] = useState<string>("rgba(20,156,206,0.2)");
    const [weight, setWeight] = useState(0);
    const [columnStatusId, setColumnStatusId] = useState(2);

    const addProxyColumnPermission = useMemo(
        () => hasPermission(userAccess, "ColumnProxyAction", "create"),
        [userAccess]
    );

    const handleChangeColumnName = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setNewColumnName(e.target.value);
    }, []);

    const handleChangeProxyBoardName = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setProxyBoardName(e.target.value);
    }, []);

    const handleEditWeight = useCallback((newWeight: number) => {
        setWeight(newWeight);
    }, []);

    const handleEditStatus = useCallback((status: number) => {
        setColumnStatusId(status);
    }, []);

    const handleToggleProxyColumn = useCallback(() => {
        if (addProxyColumnPermission) {
            setAddProxyColumn(prev => !prev);
        } else {
            showToast("У вас нет прав на создание прокси колонки!");
        }
    }, [addProxyColumnPermission, showToast]);

    const handleColorSelect = useCallback((color: string) => {
        setColumnColor(color);
    }, []);

    const onAddColumn = useCallback(() => {
        if (newColumnName.trim().length < MIN_CHARACTERS) {
            showToast(`Имя должно быть больше ${MIN_CHARACTERS} символов`);
            return;
        }
        if (!columnColor) {
            showToast("Не выбран цвет");
            return;
        }

        boardContext.handleAddColumn(
            newColumnName,
            columnColor,
            weight,
            columnStatusId,
            undefined,
            addProxyColumn ? (proxyBoardName?.length ? proxyBoardName : newColumnName) : undefined
        );
        
        setProxyBoardName("");
        setNewColumnName("");
        setAddProxyColumn(false);
    }, [
        newColumnName, 
        columnColor, 
        weight, 
        columnStatusId, 
        addProxyColumn, 
        proxyBoardName, 
        boardContext.handleAddColumn, 
        showToast
    ]);

    const disabledStyle = useMemo(() => ({ color: "rgb(175, 175, 175)" }), []);
    const disabledBorderStyle = useMemo(() => ({ borderColor: "rgb(175, 175, 175)" }), []);

    return (
        <div className="create__column--block">
            <div className="create__column--block--name">
                <input
                    type="text"
                    value={newColumnName}
                    onChange={handleChangeColumnName}
                    placeholder="Название колонки"
                />
            </div>
            
            <div className="create__column--block--color">
                <div className="create__column--block--color__name">Цвет колонки</div>
                <ColorPicker 
                    onColorSelected={handleColorSelect} 
                    options={boardContext.colorOptions} 
                    defaultColor={columnColor}
                />
            </div>
            
            <ColumnWeightInput 
                onChangeWeight={handleEditWeight} 
                edit={false} 
            />
            
            {editStatusAccess && (
                <ColumnStatusChange
                    onEditStatus={handleEditStatus}
                    currentStatus={columnStatusId}
                    edit={editStatusAccess}
                />
                
            )}
            
            {!boardContext.isProxyBoard && (
                <div className="create__column--block--proxy">
                    <label style={addProxyColumnPermission ? undefined : disabledStyle}>
                        Прокси-колонка
                    </label>
                    <input
                        style={addProxyColumnPermission ? undefined : disabledBorderStyle}
                        type="checkbox"
                        checked={addProxyColumn}
                        onChange={handleToggleProxyColumn}
                    />
                </div>
            )}
            
            {addProxyColumn && (
                <div className="create__column--block--proxy__input">
                    <input
                        value={proxyBoardName}
                        onChange={handleChangeProxyBoardName}
                        placeholder="Название доски"
                    />
                </div>
            )}
            
            <div className="create__column--block--button">
                <button id="saveBtn" onClick={onAddColumn}>
                    Сохранить
                </button>
            </div>
        </div>
    );
};

export default React.memo(CreateColumnForm);